/* ============================================================================
   NEOGEN — LEGAL / POLICY PAGE progressive enhancement
   ----------------------------------------------------------------------------
   Ported 2026-07-22 (Fable 5) from
   codex-handoffs/outbound/2026-07-07-support-legal-template-mockup-FABLE.html.

   Shopify renders /policies/* inside theme.liquid with fixed markup
   (.shopify-policy__container > __title + __body). This script upgrades that
   DOM to the mockup's composition:
     1. masthead (eyebrow + title + counsel-review banner) above a hairline
     2. grid body: sticky side-rail TOC (built from the body's h2s) + column
   Pure enhancement: with JS off, assets/neogen-policy.css still renders a
   clean single-column legal page. Policy COPY lives in Shopify admin
   (Settings → Policies) — this file never touches wording.

   COUNSEL BANNER: customer-facing per the locked mockup. Set SHOW_COUNSEL
   to false (one flag) when counsel sign-off lands and Craig wants it gone.
   ============================================================================ */
(function () {
  'use strict';

  var SHOW_COUNSEL = false;
  var EYEBROW_TEXT = 'Legal';
  var COUNSEL_MARK = 'For review';
  var COUNSEL_TEXT =
    'Reviewed by counsel before publish. This page states our policy in plain terms; it is not legal advice.';
  var TOC_MIN_HEADINGS = 3;

  /* ---- smart quotes -------------------------------------------------------
     Progressive typographic pass: straight quotes/apostrophes → curly in the
     policy body's visible TEXT NODES only. Never touches attributes, URLs,
     or code/pre content. Standard rules: a quote after start-of-block,
     whitespace, or opening punctuation opens; everything else closes
     (apostrophes fall out of the closing case). */
  var SQ_SKIP = { SCRIPT: 1, STYLE: 1, CODE: 1, PRE: 1, KBD: 1, SAMP: 1, TEXTAREA: 1, SVG: 1 };
  var SQ_BLOCK = /^(P|DIV|LI|UL|OL|DL|DT|DD|H[1-6]|TD|TH|TR|THEAD|TBODY|TABLE|SECTION|ARTICLE|BLOCKQUOTE|FIGURE|FIGCAPTION|HEADER|FOOTER|NAV)$/;
  var SQ_OPEN_BEFORE = /[\s (\[{—–‘“-]/;

  function smartQuotes(root) {
    function blockOf(node) {
      var p = node.parentElement;
      while (p && p !== root && !SQ_BLOCK.test(p.tagName)) p = p.parentElement;
      return p;
    }
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        var p = n.parentElement;
        while (p && p !== root) {
          if (SQ_SKIP[p.tagName]) return NodeFilter.FILTER_REJECT;
          p = p.parentElement;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var prev = '';
    var prevBlock = null;
    var node;
    function opens(ch) { return ch === '' || SQ_OPEN_BEFORE.test(ch); }
    while ((node = walker.nextNode())) {
      var block = blockOf(node);
      if (block !== prevBlock) { prev = ''; prevBlock = block; }
      var text = node.nodeValue;
      if (!text) continue;
      if (text.indexOf('"') === -1 && text.indexOf("'") === -1) {
        prev = text.charAt(text.length - 1);
        continue;
      }
      var out = '';
      for (var i = 0; i < text.length; i++) {
        var c = text.charAt(i);
        if (c === '"') c = opens(prev) ? '“' : '”';
        else if (c === "'") c = opens(prev) ? '‘' : '’';
        out += c;
        prev = c;
      }
      if (out !== text) node.nodeValue = out;
    }
  }

  /* ---- table scroll wrap --------------------------------------------------
     Wide disclosure tables (CCPA) scroll sideways on small screens inside a
     .legal__table-scroll shell instead of overflowing the reading column. */
  function wrapTables(root) {
    [].slice.call(root.querySelectorAll('table')).forEach(function (table) {
      if (table.parentElement && table.parentElement.classList.contains('legal__table-scroll')) return;
      var shell = document.createElement('div');
      shell.className = 'legal__table-scroll';
      table.parentNode.insertBefore(shell, table);
      shell.appendChild(table);
    });
  }

  /* ---- TOC scrollspy ------------------------------------------------------
     Light rail indicator: a short copper segment on the TOC's hairline rail
     tracks the section currently in view; the active link gets color/weight.
     IntersectionObserver fires as headings cross the reading line; the
     handler then picks the last heading above that line (deterministic on
     jumps, clicks, and reload-mid-page). Marker motion is CSS-transitioned;
     prefers-reduced-motion disables the transition so it simply jumps. */
  function initScrollspy(ol, headings) {
    if (!('IntersectionObserver' in window)) return;
    var links = [].slice.call(ol.querySelectorAll('a'));
    if (!links.length) return;
    var byId = {};
    links.forEach(function (a) { byId[a.getAttribute('href').slice(1)] = a; });

    var marker = document.createElement('span');
    marker.className = 'legal__toc-marker';
    marker.setAttribute('aria-hidden', 'true');
    ol.appendChild(marker);

    var READ_LINE = 140; /* px from viewport top ≈ just under sticky offset */
    var active = null;

    function placeMarker() {
      /* Recomputed on EVERY update, not only on active change: offsets go
         stale after viewport resizes (TOC is display:none under 900px, where
         offsetTop reads 0) and after webfont reflow. */
      if (!active || active.offsetParent === null) { marker.style.opacity = '0'; return; }
      /* a.offsetTop is relative to the position:relative <ol> */
      marker.style.transform = 'translateY(' + (active.offsetTop + 2) + 'px)';
      marker.style.opacity = '1';
    }

    function setActive(link) {
      if (link !== active) {
        if (active) {
          active.classList.remove('is-active');
          active.removeAttribute('aria-current');
        }
        active = link;
        if (link) {
          link.classList.add('is-active');
          link.setAttribute('aria-current', 'true');
        }
      }
      placeMarker();
    }

    function update() {
      var current = null;
      for (var i = 0; i < headings.length; i++) {
        if (headings[i].getBoundingClientRect().top <= READ_LINE) current = headings[i];
        else break;
      }
      if (!current) current = headings[0];
      setActive(current ? byId[current.id] : null);
    }

    var io = new IntersectionObserver(update, {
      rootMargin: '-' + READ_LINE + 'px 0px -45% 0px',
      threshold: [0, 1]
    });
    headings.forEach(function (h) { io.observe(h); });
    window.addEventListener('hashchange', function () { window.requestAnimationFrame(update); });
    window.addEventListener('resize', function () { window.requestAnimationFrame(update); });
    window.addEventListener('load', update);
    update();
  }

  function init() {
    var container = document.querySelector('.shopify-policy__container');
    if (!container || container.classList.contains('legal')) return;
    var titleWrap = container.querySelector('.shopify-policy__title');
    var body = container.querySelector('.shopify-policy__body');
    if (!titleWrap || !body) return;

    container.classList.add('legal');

    /* ---- masthead -------------------------------------------------------- */
    var masthead = document.createElement('header');
    masthead.className = 'legal__masthead';
    var inner = document.createElement('div');
    inner.className = 'legal__masthead-inner';
    masthead.appendChild(inner);

    var eyebrow = document.createElement('span');
    eyebrow.className = 'legal__eyebrow';
    eyebrow.textContent = EYEBROW_TEXT;
    inner.appendChild(eyebrow);

    var h1 = titleWrap.querySelector('h1');
    if (h1) {
      h1.classList.add('legal__title');
      inner.appendChild(h1);
    }
    titleWrap.remove();

    if (SHOW_COUNSEL) {
      var counsel = document.createElement('p');
      counsel.className = 'legal__counsel';
      var mark = document.createElement('span');
      mark.className = 'legal__counsel-mark';
      mark.textContent = COUNSEL_MARK;
      var text = document.createElement('span');
      text.textContent = COUNSEL_TEXT;
      counsel.appendChild(mark);
      counsel.appendChild(text);
      inner.appendChild(counsel);
    }

    /* ---- grid body: TOC rail + reading column ---------------------------- */
    /* smart quotes run BEFORE the TOC is built so link labels copied from the
       headings carry the curled glyphs too (slugs are unaffected: apostrophes
       of either kind strip to the same id). */
    smartQuotes(body);

    var grid = document.createElement('div');
    grid.className = 'legal__body';

    var headings = [].slice.call(body.querySelectorAll('h2'));
    var used = {};
    function slugify(str) {
      var s = String(str).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'section';
      if (used[s]) { used[s] += 1; s = s + '-' + used[s]; } else { used[s] = 1; }
      return s;
    }

    var tocList = null;
    if (headings.length >= TOC_MIN_HEADINGS) {
      var toc = document.createElement('nav');
      toc.className = 'legal__toc';
      toc.setAttribute('aria-label', 'On this page');
      var label = document.createElement('p');
      label.className = 'legal__toc-label';
      label.textContent = 'On this page';
      toc.appendChild(label);
      var ol = document.createElement('ol');
      headings.forEach(function (h) {
        if (!h.id) h.id = slugify(h.textContent);
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.href = '#' + h.id;
        a.textContent = h.textContent.trim();
        li.appendChild(a);
        ol.appendChild(li);
      });
      toc.appendChild(ol);
      grid.appendChild(toc);
      tocList = ol;
    } else {
      grid.classList.add('legal__body--no-toc');
    }

    grid.appendChild(body);
    container.insertBefore(masthead, container.firstChild);
    container.appendChild(grid);

    /* table + scrollspy enhancements (after DOM is attached) */
    wrapTables(body);
    if (tocList) initScrollspy(tocList, headings);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
